import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { recoverPassword, recoverPasswordSchema, sendVerificationCode } from "@/lib/client/auth";
import FormErrorMessage from "@/features/shared/components/form-error-message";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export function RecoverPasswordDialogContent({ close }: { close: () => void }) {
  const {
    mutate: sendCode,
    isPending: sendCodePending,
    isSuccess: sendCodeSuccess,
    reset: resetSendCode,
  } = useMutation({
    mutationFn: (value: { email: string }) => sendVerificationCode(value),
    onSuccess() {
      toast.success(
        "验证码已发送，如果邮箱已注册过，则可以在邮箱里看到验证码（也可能在垃圾邮件中）",
      );
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (value: {
      email: string;
      verificationCode: string;
      newPassword: string;
      reNewPassword: string;
    }) => recoverPassword(value),
    onSuccess() {
      toast.success("密码设置成功");
      close();
    },
    onError(error) {
      toast.error(error.message);
    },
  });

  const form = useForm({
    defaultValues: {
      email: "",
      verificationCode: "",
      newPassword: "",
      reNewPassword: "",
    },
    validators: {
      onChange: recoverPasswordSchema,
    },
    onSubmit({ value }) {
      mutate(value, {
        onSuccess() {
          form.reset();
        },
      });
    },
  });
  return (
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <DialogTitle>找回密码</DialogTitle>
        <DialogDescription>找回你的账户密码</DialogDescription>
      </DialogHeader>

      <form
        id="recover-password-form"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="space-y-4"
      >
        <form.Field
          name="email"
          children={({ state, handleBlur, handleChange }) => (
            <div className="space-y-1">
              <label className="text-sm" htmlFor="email">
                邮箱
              </label>
              <div className="relative">
                <Input
                  value={state.value}
                  onChange={(e) => {
                    handleChange(e.target.value);
                    resetSendCode();
                  }}
                  onBlur={handleBlur}
                  id="email"
                  name="email"
                  className={cn("pr-20")}
                />

                <form.Subscribe
                  selector={(state) =>
                    [
                      state.values.email,
                      state.fieldMeta.email?.isTouched,
                      state.fieldMeta.email?.isValid,
                    ] as const
                  }
                  children={([email, isTouched, isValid]) => (
                    <Button
                      type="button"
                      size="sm"
                      className="absolute inset-y-0 right-1 flex items-center justify-center p-1 h-fit top-1/2 -translate-y-1/2"
                      onClick={() => {
                        sendCode({ email });
                      }}
                      disabled={!isTouched || !isValid || sendCodeSuccess || sendCodePending}
                    >
                      {sendCodePending ? "发送中..." : sendCodeSuccess ? "发送成功" : "发送验证码"}
                    </Button>
                  )}
                />
              </div>
              {state.meta.isTouched && <FormErrorMessage errors={state.meta.errors} />}
            </div>
          )}
        />

        <form.Field
          name="verificationCode"
          children={({ state, handleBlur, handleChange }) => (
            <div className="space-y-1">
              <label className="text-sm" htmlFor="verificationCode">
                验证码
              </label>
              <Input
                value={state.value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                id="verificationCode"
                name="verificationCode"
                type="text"
              />
              {state.meta.isTouched && <FormErrorMessage errors={state.meta.errors} />}
            </div>
          )}
        />

        <form.Field
          name="newPassword"
          children={({ state, handleBlur, handleChange }) => (
            <>
              <Input
                value={state.value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder="新密码"
              />
              {state.meta.isTouched && <FormErrorMessage errors={state.meta.errors} />}
            </>
          )}
        />

        <form.Field
          name="reNewPassword"
          children={({ state, handleBlur, handleChange }) => (
            <>
              <Input
                value={state.value}
                onChange={(e) => handleChange(e.target.value)}
                onBlur={handleBlur}
                placeholder="重复新密码"
              />
              {state.meta.isTouched && <FormErrorMessage errors={state.meta.errors} />}
            </>
          )}
        />
      </form>

      <DialogFooter>
        <Button form="recover-password-form" type="submit" disabled={isPending}>
          {isPending && <Loader2 className="animate-spin" />}
          确定
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
